import { useCallback, useEffect, useMemo } from 'react';
import type { ApolloError } from '@apollo/client';
import { useGetVegaIntegrationStatusQuery } from '@gonfalon/observability-graphql/hooks';

import { blockedGitHubAuthorization, isGitHubUserAuthorized } from '../githubAuthorization';
import { buildGitHubConnectUrl } from '../githubConnectUrl';
import { trackGitHubConnectClicked } from '../internal/githubIntegrationAnalytics';
import type { GitHubRepo } from '../types';

/**
 * `not-installed` and `not-authorized` both mean "can't use GitHub yet" but need different
 * remedies: installing the app account-wide vs. this member authorizing it. `installationId` is
 * the only signal that separates them — an uninstalled account can still carry an `authLink`, and
 * in that case the link points at the install flow.
 */
export type GitHubIntegrationState =
  | 'loading'
  | 'error'
  | 'not-installed'
  | 'not-authorized'
  | 'installation-inaccessible'
  | 'github-unavailable'
  | 'authorization-unknown'
  | 'ready';

export interface ConnectGitHubOptions {
  /** Where GitHub returns to. Defaults to the current URL. */
  returnUrl?: string;
  /** Surface name attached to the connect analytics event. */
  component?: string;
}

export interface GitHubIntegration {
  state: GitHubIntegrationState;
  isLoading: boolean;
  isReady: boolean;
  allRepos: GitHubRepo[];
  privateRepos: GitHubRepo[];
  privateRepoKeys: string[];
  authLink: string | undefined;
  error: ApolloError | undefined;
  connect: (options?: ConnectGitHubOptions) => void;
  refetch: () => void;
}

/**
 * Project-scoped GitHub App status, plus the connect action that goes with it.
 *
 * Collapses the four hand-rolled derivations of "is GitHub connected" that disagreed on what to do
 * while the query was still in flight. Callers get one `state` to switch on instead.
 *
 * See `useGitHubAppIntegrationStatus` for the account-scoped variant used by the settings pages.
 */
export function useGitHubIntegration(projectId: string): GitHubIntegration {
  const { data, error, loading, refetch } = useGetVegaIntegrationStatusQuery({
    variables: { project_id: projectId },
  });

  const status = data?.vega_integration_status;
  const allRepos = useMemo(() => status?.repositories ?? [], [status?.repositories]);
  const privateRepos = useMemo(() => allRepos.filter((repo) => !repo.is_public), [allRepos]);
  const privateRepoKeys = useMemo(() => privateRepos.map((repo) => repo.key), [privateRepos]);

  // Connecting navigates out to GitHub and back, and the browser may restore this page from cache
  // rather than re-running the query — which would leave it claiming GitHub is still disconnected.
  // Same reason `useGitHubAppIntegrationStatus` listens for this.
  useEffect(() => {
    const handlePageShow = () => void refetch();
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [refetch]);

  // Loading only while a request is genuinely in flight with nothing to show yet. Checking for an
  // absent result alone would strand the hook in `loading` forever whenever Apollo settles without
  // data or an error, and every caller hides its connect affordance while loading.
  const isLoading = loading && data === undefined;
  const authLink = status?.authLink ?? undefined;

  // A result we already have beats a later failure: Apollo surfaces cached data alongside the
  // error when a refetch fails, and treating that as `error` would drop a connected member back to
  // "not connected" — hiding their repositories and blocking cleanup until a refresh succeeds.
  // `error` still comes back on the result for callers that key permission handling off it.
  let state: GitHubIntegrationState;
  if (isLoading) {
    state = 'loading';
  } else if (data !== undefined && !status?.installationId) {
    state = 'not-installed';
  } else if (isGitHubUserAuthorized(status?.authorizationState, status?.userTokenValid)) {
    state = 'ready';
  } else if (data !== undefined) {
    state = blockedGitHubAuthorization(status?.authorizationState);
  } else if (error) {
    state = 'error';
  } else {
    state = 'not-installed';
  }

  const connect = useCallback(
    ({ returnUrl, component }: ConnectGitHubOptions = {}) => {
      trackGitHubConnectClicked({ component });
      window.location.href = buildGitHubConnectUrl({
        flow: state === 'not-authorized' || state === 'installation-inaccessible' ? 'authorize' : 'install',
        returnUrl,
      });
    },
    [state],
  );

  return {
    state,
    isLoading,
    isReady: state === 'ready',
    allRepos,
    privateRepos,
    privateRepoKeys,
    authLink,
    error,
    connect,
    refetch: useCallback(() => void refetch(), [refetch]),
  };
}
